import { PERSONAL_STATUS_CATEGORY_KEY } from '../config/postComposeConstants';
import { getStarterClubPickerRows } from '../config/starterClubCatalog';
import { discussionOwnerUserId, memberPreviewFromThread, authorSnapshotProfilePictureUrl } from './discussionUtils';

const MAX_PEOPLE = 16;
const MAX_CLUBS = 8;
const MAX_GUEST_PEOPLE = 2;
const MAX_THREAD_SCAN = 80;

function personSuggestionRow(raw) {
  const userId = String(raw?.userId || '').trim();
  if (!userId) return null;
  const name = String(raw?.name || '').trim() || 'Member';
  return {
    kind: 'person',
    id: `person:${userId}`,
    userId,
    name,
    profilePictureUrl: raw?.profilePictureUrl || '',
    profileMode: raw?.profileMode || 'personal',
  };
}

function clubRankMap(rows) {
  const map = new Map();
  (rows || []).forEach((row, index) => {
    const slug = String(row?.slug || '').trim();
    if (slug && !map.has(slug)) map.set(slug, index);
  });
  return map;
}

export function collectSuggestionPeople({
  threads = [],
  topContributors = [],
  viewerUserId = '',
  loggedIn = false,
} = {}) {
  const viewer = String(viewerUserId || '').trim();
  const seen = new Set(viewer ? [viewer] : []);
  const people = [];

  const pushPerson = (raw) => {
    const userId = String(raw?.userId || '').trim();
    if (!userId || seen.has(userId)) return;
    seen.add(userId);
    const row = personSuggestionRow(raw);
    if (row) people.push(row);
  };

  for (const row of topContributors || []) {
    pushPerson(row);
    if (people.length >= MAX_PEOPLE) break;
  }

  if (people.length < MAX_PEOPLE) {
    let scanned = 0;
    for (const thread of threads || []) {
      if (scanned >= MAX_THREAD_SCAN) break;
      scanned += 1;
      if (thread?.postAnonymous) continue;
      const preview = memberPreviewFromThread(thread);
      const userId = String(preview?.userId || discussionOwnerUserId(thread) || '').trim();
      if (!userId) continue;
      pushPerson({
        userId,
        name: preview?.name || thread?.authorSnapshot?.name,
        profilePictureUrl:
          preview?.profilePictureUrl || authorSnapshotProfilePictureUrl(thread?.authorSnapshot),
        profileMode: preview?.profileMode || thread?.authorSnapshot?.profileMode,
      });
      if (people.length >= MAX_PEOPLE) break;
    }
  }

  const cap = loggedIn ? MAX_PEOPLE : MAX_GUEST_PEOPLE;
  return people.slice(0, cap);
}

function toClubCandidate(club, extras = {}) {
  const slug = String(club?.slug || club?.key || extras.slug || '').trim();
  if (!slug || slug === PERSONAL_STATUS_CATEGORY_KEY) return null;
  return {
    slug,
    clubId: club?.clubId,
    name: club?.name || club?.label || slug,
    thumbnailUrl: club?.thumbnailUrl || '',
    memberCount: Number(club?.memberCount) || 0,
    topicCount: Number(club?.topicCount || club?.count) || 0,
    isStarter: !!club?.isStarter || !!extras.isStarter,
    joined: !!club?.joined,
    isOwner: !!club?.isOwner,
    disabled: !!club?.disabled,
  };
}

function sortClubCandidates(candidates, activityRank, popularRank) {
  return [...candidates].sort((a, b) => {
    if (!!a.joined !== !!b.joined) return a.joined ? 1 : -1;
    const aActive = activityRank.has(a.slug) ? activityRank.get(a.slug) : 99;
    const bActive = activityRank.has(b.slug) ? activityRank.get(b.slug) : 99;
    if (aActive !== bActive) return aActive - bActive;
    const aPop = popularRank.has(a.slug) ? popularRank.get(a.slug) : 99;
    const bPop = popularRank.has(b.slug) ? popularRank.get(b.slug) : 99;
    if (aPop !== bPop) return aPop - bPop;
    const byMembers = (Number(b.memberCount) || 0) - (Number(a.memberCount) || 0);
    if (byMembers !== 0) return byMembers;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
}

export function collectSuggestionClubs({
  clubsBrowse = [],
  topClubs = [],
  mostActiveClubs = [],
} = {}) {
  const activityRank = clubRankMap(mostActiveClubs);
  const popularRank = clubRankMap(topClubs);
  const bySlug = new Map();
  const add = (raw, extras) => {
    const candidate = toClubCandidate(raw, extras);
    if (!candidate || candidate.disabled || candidate.isOwner) return;
    if (!bySlug.has(candidate.slug)) bySlug.set(candidate.slug, candidate);
  };

  for (const club of clubsBrowse || []) add(club);
  for (const club of mostActiveClubs || []) add(club);
  for (const club of topClubs || []) add(club);
  if (bySlug.size === 0) {
    for (const row of getStarterClubPickerRows()) add(row, { isStarter: true });
  }
  if (bySlug.size === 0) {
    add({ key: 'general', label: 'The Public Square' }, { isStarter: true });
    add({ key: 'announcements', label: 'Announcements' }, { isStarter: true });
  }

  const sorted = sortClubCandidates([...bySlug.values()], activityRank, popularRank);
  const unjoined = sorted.filter((club) => !club.joined);
  const picked = (unjoined.length >= 3 ? unjoined : sorted).slice(0, MAX_CLUBS);

  return picked.map((club) => ({
    kind: 'club',
    id: `club:${club.clubId || club.slug}`,
    slug: club.slug,
    clubId: club.clubId,
    name: club.name || club.slug,
    thumbnailUrl: club.thumbnailUrl || '',
    memberCount: Number(club.memberCount) || 0,
    topicCount: Number(club.topicCount) || 0,
    isStarter: !!club.isStarter,
    joined: !!club.joined,
  }));
}

/** Merge seed + API people; seed rows first, then extras (e.g. feed-people-suggestions). */
export function mergeSuggestionPeople(seedList = [], extraList = [], { viewerUserId = '', max = MAX_PEOPLE } = {}) {
  const viewer = String(viewerUserId || '').trim();
  const seen = new Set(viewer ? [viewer] : []);
  const out = [];
  for (const raw of [...seedList, ...extraList]) {
    const userId = String(raw?.userId || '').trim();
    if (!userId || seen.has(userId)) continue;
    seen.add(userId);
    const row = personSuggestionRow(raw);
    if (row) out.push(row);
    if (out.length >= max) break;
  }
  return out;
}

export function interleaveSuggestionItems(people, clubs, loggedIn) {
  const peopleList = Array.isArray(people) ? people : [];
  const clubList = Array.isArray(clubs) ? clubs : [];
  if (!loggedIn) {
    return [...clubList, ...peopleList.slice(0, MAX_GUEST_PEOPLE)];
  }
  const mixed = [];
  let i = 0;
  let j = 0;
  while (i < peopleList.length || j < clubList.length) {
    if (i < peopleList.length) mixed.push(peopleList[i++]);
    if (j < clubList.length) mixed.push(clubList[j++]);
  }
  return mixed;
}

export function shuffleSuggestionItems(items) {
  const next = [...(items || [])];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}
